import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import axios from "../../../utils/axiosInstance";
import { ACTION, usePermissions } from "../../../utils/permissions";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import SearchIcon from "@mui/icons-material/Search";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { alpha } from "@mui/material/styles";

type DsarRequest = {
    requestId: string;
    email: string;
    requestDate: number | string;
    requestType: string;
    verificationStatus: string;
    requestStatus: string;
    requestRaisedBy: string;
    daysRemaining?: number;
    details?: string;
    files?: Array<{ name?: string; url?: string }> | string[] | null;
    /** S3-backed attachments (presigned-url?fileIndex=…); present on list/detail from API. */
    downloadableAuthAttachmentCount?: number;
};

const STATUS_OPTIONS: string[] = [
    "Initiated",
    "In Progress",
    "Report Received",
    "Report Sent",
    "Re-opened",
];

/** Forward workflow ranks (Initiated and Re-opened are entry points). */
const STATUS_RANK: Record<string, number> = {
    Initiated: 0,
    "Re-opened": 0,
    "In Progress": 1,
    "Report Received": 2,
    "Report Sent": 3,
};

function isValidEmailFormat(value: string): boolean {
    const v = value.trim();
    if (!v) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

/** CMS API uses `Report Sent`; `Request Sent` accepted if present in data. */
const SEND_EMAIL_REQUEST_STATUSES = new Set(["Report Sent", "Request Sent"]);

function canSendDsarEmail(req: DsarRequest): boolean {
    const st = `${req.requestStatus || ""}`.trim();
    return (
        SEND_EMAIL_REQUEST_STATUSES.has(st) && isValidEmailFormat(req.email || "")
    );
}

/** Default must match `DSAR_PRIVACY_REPORT_ATTACHMENT_MAX_MB_DEFAULT` in capra-canvas-service. */
const MAX_PRIVACY_REPORT_ATTACHMENT_BYTES = 10 * 1024 * 1024;

const PRIVACY_REPORT_ATTACHMENT_MIME = new Set([
    "application/pdf",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
]);

function validatePrivacyReportAttachmentFile(file: File): string | null {
    if (file.size > MAX_PRIVACY_REPORT_ATTACHMENT_BYTES) {
        return `File must be at most ${Math.floor(
            MAX_PRIVACY_REPORT_ATTACHMENT_BYTES / (1024 * 1024),
        )} MB.`;
    }
    const ext = file.name.includes(".") ? file.name.split(".").pop()?.toLowerCase() : "";
    const mimeOk = !file.type || PRIVACY_REPORT_ATTACHMENT_MIME.has(file.type);
    const extOk = ext === "pdf" || ext === "docx";
    if (!mimeOk && !extOk) {
        return "Use a PDF or DOCX report file.";
    }
    return null;
}

/** Body `reportTypeLabel` for POST /dsar/privacy-report-delivery (HTML template in capra-canvas-service). */
function privacyReportEmailReportType(req: DsarRequest): "Access" | "Correction" | null {
    const t = `${req.requestType || ""}`.trim();
    if (!t || t === "-") return null;
    if (/correction/i.test(t) || t === "Data Correction") return "Correction";
    if (/access/i.test(t) || t === "Data Access") return "Access";
    return null;
}

function allowedNextStatuses(current = "Initiated"): string[] {
    const all = STATUS_OPTIONS;
    let out: string[];
    if (current === "Report Sent") {
        out = ["Report Sent", "Re-opened"];
    } else {
        out = all.filter((s) => {
            if (s === current) return true;
            if (s === "Re-opened") return false;
            const rc = STATUS_RANK[current];
            const rs = STATUS_RANK[s];
            if (rc === undefined || rs === undefined) return false;
            return rs > rc;
        });
    }
    if (!out.includes(current)) {
        return [current, ...out];
    }
    return out;
}

/** List / filter API values (unchanged for compatibility with existing DSAR list API). */
const REQUEST_TYPE_OPTIONS: string[] = ["Access", "Correction"];
const VERIFICATION_OPTIONS: string[] = ["Incomplete", "Complete"];

/** Add New Request modal — labels per product spec. */
const CREATE_REQUEST_TYPE_OPTIONS = ["Data Access", "Data Correction"] as const;
const CREATE_REQUEST_BY_OPTIONS = ["User", "Agent"] as const;

/** Authorization file limits (adjust if backend publishes different limits). */
const MAX_AUTH_FILE_BYTES = 10 * 1024 * 1024;
/** Must stay within capra-canvas-service `DSAR_AUTH_DOCUMENT_MAX_FILES` (default 5). */
const MAX_AUTH_FILES_PER_REQUEST = 5;
const ALLOWED_AUTH_MIME = new Set([
    "application/pdf",
    "image/jpeg",
    "image/png",
    "image/jpg",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
]);

/** Requested date cannot be older than this many days; must match `MAX_REQUEST_DATE_LOOKBACK_DAYS` in capra-canvas-service `dsar.constants.js` (~2 years). */
const MAX_REQUESTED_DATE_LOOKBACK_DAYS = 2 * 365;

/** Must match `DETAILS_MAX_LENGTH` in capra-canvas-service `app/utils/constants/dsar.constants.js`. */
const DSAR_CREATE_DETAILS_MAX_LENGTH = 2000;

/** DSAR countdown: starts at this value and drops by 1 each full 24h from request creation (User or Agent). */
const DSAR_INITIAL_DAYS_REMAINING = 45;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** API canonical values (POST /dsar JSON); UI may use wireframe labels. */
function mapUiRequestTypeToApi(ui: string): "Access" | "Correction" {
    if (ui === "Data Access") return "Access";
    if (ui === "Data Correction") return "Correction";
    if (ui === "Access" || ui === "Correction") return ui;
    return "Access";
}

function isCorrectionRequestType(ui: string): boolean {
    return ui === "Data Correction" || ui === "Correction";
}

function last<T>(arr: T[]): T | undefined {
    return arr.length ? arr[arr.length - 1] : undefined;
}

function formatAuthFileSelectionSummary(count: number): string {
    if (count === 0) return "No files chosen";
    const suffix = count === 1 ? "" : "s";
    return `${count} file${suffix} selected`;
}

function authFileFingerprint(f: File): string {
    return `${f.name}\0${f.size}\0${f.lastModified}`;
}

/** Returns an error message or null if the file passes checks. */
function validateSingleAuthFile(file: File): string | null {
    if (file.size > MAX_AUTH_FILE_BYTES) {
        return `"${file.name}" exceeds the maximum file size (${Math.floor(
            MAX_AUTH_FILE_BYTES / (1024 * 1024)
        )} MB).`;
    }
    const ext = file.name.includes(".") ? file.name.split(".").pop()?.toLowerCase() : "";
    const allowedExt = ["pdf", "jpg", "jpeg", "png", "doc", "docx"];
    const mimeOk = !file.type || ALLOWED_AUTH_MIME.has(file.type);
    const extOk = Boolean(ext && allowedExt.includes(ext));
    if (!mimeOk && !extOk) {
        return `"${file.name}" is not a supported type. Use PDF, JPG, PNG, DOC, or DOCX.`;
    }
    return null;
}

/**
 * Appends new picks to existing files (same field repeated in multipart as `authDocument`).
 * Caps at MAX_AUTH_FILES_PER_REQUEST (aligned with CMS DSAR API).
 */
function mergeAuthFiles(prev: File[], incoming: File[]): { files: File[]; error: string } {
    const merged: File[] = [...prev];
    const seen = new Set(merged.map(authFileFingerprint));

    for (const file of incoming) {
        const fp = authFileFingerprint(file);
        if (seen.has(fp)) continue;

        if (merged.length >= MAX_AUTH_FILES_PER_REQUEST) {
            return {
                files: merged,
                error: `You can attach at most ${MAX_AUTH_FILES_PER_REQUEST} files per request.`,
            };
        }
        const err = validateSingleAuthFile(file);
        if (err) return { files: prev, error: err };
        merged.push(file);
        seen.add(fp);
    }
    return { files: merged, error: "" };
}

function toMillis(d: number | string): number {
    if (typeof d === "number") return d;
    const parsed = Date.parse(d);
    return Number.isFinite(parsed) ? parsed : 0;
}

function formatDate(d: number | string) {
    const ms = toMillis(d);
    if (!ms) return "-";
    return dayjs(ms).format("MMM DD, YYYY");
}

function formatAuditTimestamp(d: number | string) {
    const ms = toMillis(d);
    if (!ms) return "-";
    return dayjs(ms).format("MMM DD, YYYY HH:mm:ss");
}

function extractAuditEntries(payload: any): any[] {
    if (!payload) return [];
    if (Array.isArray(payload.entries)) return payload.entries;
    if (Array.isArray(payload.audit)) return payload.audit;
    if (Array.isArray(payload.items)) return payload.items;
    if (Array.isArray(payload)) return payload;
    return [];
}

function computedDaysRemaining(req: DsarRequest) {
    const createdAt = toMillis(req.requestDate);
    if (createdAt) {
        const daysElapsed = Math.floor((Date.now() - createdAt) / MS_PER_DAY);
        return Math.max(0, DSAR_INITIAL_DAYS_REMAINING - Math.max(0, daysElapsed));
    }
    if (typeof req.daysRemaining === "number" && Number.isFinite(req.daysRemaining)) {
        return Math.max(0, Math.floor(req.daysRemaining));
    }
    return DSAR_INITIAL_DAYS_REMAINING;
}

function hasFiles(req: DsarRequest) {
    if (!req.files) return false;
    if (Array.isArray(req.files)) return req.files.length > 0;
    return false;
}

/** Presigned-download count (S3 keys). Falls back when older API responses omit the field. */
function downloadableAttachmentCountForRow(req: DsarRequest): number {
    const n = req.downloadableAuthAttachmentCount;
    if (typeof n === "number" && Number.isFinite(n) && n >= 0) {
        return n;
    }
    return hasFiles(req) ? 1 : 0;
}

function authDownloadButtonLabel(loading: boolean, total: number, index: number): string {
    if (loading) return "Loading…";
    if (total === 1) return "Download";
    return `File ${index + 1}`;
}

function normalizeFiles(req: DsarRequest): Array<{ name: string; url: string }> {
    if (!req.files || !Array.isArray(req.files)) return [];
    return req.files
        .map((f: any, idx: number) => {
            if (typeof f === "string") return { name: `File ${idx + 1}`, url: f };
            return {
                name: f?.name || `File ${idx + 1}`,
                url: f?.url || "",
            };
        })
        .filter((f) => !!f.url);
}

/** Center modals in the viewport; symmetric horizontal margin on narrow screens. */
const DSAR_DIALOG_LAYOUT_PROPS = {
    sx: {
        "& .MuiDialog-container": {
            alignItems: "center",
            justifyContent: "center",
        },
    },
    PaperProps: {
        sx: { mx: 2 },
    },
};

export default function DSARDashboard() {
    const { enqueueSnackbar } = useSnackbar();
    const { hasPermission } = usePermissions();

    const canView = hasPermission("dsar", ACTION.VIEW);
    const canEdit = hasPermission("dsar", ACTION.EDIT);

    const [loader, setLoader] = useState(false);
    const [requests, setRequests] = useState<DsarRequest[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [cursorQueue, setCursorQueue] = useState<string[]>([""]);
    const [nextCursor, setNextCursor] = useState<string | null>(null);
    /** Bumps on an interval so days-remaining recomputes when 24h boundaries pass without a refetch. */
    const [countdownTick, setCountdownTick] = useState(0);

    const [searchEmail, setSearchEmail] = useState("");
    const [filterRequestStatus, setFilterRequestStatus] = useState<string>("");
    const [filterVerificationStatus, setFilterVerificationStatus] =
        useState<string>("");
    const [filterRequestType, setFilterRequestType] = useState<string>("");
    /** List filter: calendar day sent as YYYY-MM-DD (server matches UTC day of stored requestDate). */
    const [filterRequestDate, setFilterRequestDate] = useState<Dayjs | null>(null);

    const [createOpen, setCreateOpen] = useState(false);
    const [createEmail, setCreateEmail] = useState("");
    /** Calendar date when the DSAR was requested (User or Agent); sent as start-of-day epoch ms. */
    const [createRequestDate, setCreateRequestDate] = useState<Dayjs | null>(() =>
        dayjs().startOf("day")
    );
    const [createRequestType, setCreateRequestType] = useState<string>("");
    const [createRequestBy, setCreateRequestBy] = useState<string>("");
    const [createAuthAgentName, setCreateAuthAgentName] = useState("");
    const [createAuthAgentEmail, setCreateAuthAgentEmail] = useState("");
    const [createAuthFiles, setCreateAuthFiles] = useState<File[]>([]);
    const [createDetails, setCreateDetails] = useState("");
    const [createFileError, setCreateFileError] = useState<string>("");
    const [createEmailError, setCreateEmailError] = useState<string>("");
    const [createSaving, setCreateSaving] = useState(false);
    const authInputRef = useRef<HTMLInputElement>(null);

    const [privacyReportSendOpen, setPrivacyReportSendOpen] = useState(false);
    const [privacyReportSendRow, setPrivacyReportSendRow] = useState<DsarRequest | null>(
        null,
    );
    const [privacyReportSendFile, setPrivacyReportSendFile] = useState<File | null>(null);
    const [privacyReportSendFileError, setPrivacyReportSendFileError] = useState("");
    const privacyReportFileInputRef = useRef<HTMLInputElement>(null);

    const authRequired = createRequestBy === "Agent";

    const subjectAgentEmailConflict = useMemo(() => {
        const agent = createAuthAgentEmail.trim();
        if (!agent) return false;
        return createEmail.trim().toLowerCase() === agent.toLowerCase();
    }, [createEmail, createAuthAgentEmail]);

    const isCreateFormValid = useMemo(() => {
        if (!isValidEmailFormat(createEmail)) return false;
        if (subjectAgentEmailConflict) return false;
        const typeOk = (CREATE_REQUEST_TYPE_OPTIONS as readonly string[]).includes(
            createRequestType
        );
        const byOk = (CREATE_REQUEST_BY_OPTIONS as readonly string[]).includes(
            createRequestBy
        );
        if (!createRequestType || !typeOk) return false;
        if (!createRequestBy || !byOk) return false;
        if (!createRequestDate?.isValid()) return false;
        if (authRequired && createAuthFiles.length === 0) return false;
        if (createAuthAgentEmail.trim() && !isValidEmailFormat(createAuthAgentEmail)) {
            return false;
        }
        if (isCorrectionRequestType(createRequestType) && !createDetails.trim()) {
            return false;
        }
        return true;
    }, [
        createEmail,
        createRequestDate,
        createRequestType,
        createRequestBy,
        createAuthAgentName,
        createAuthAgentEmail,
        authRequired,
        createAuthFiles,
        createDetails,
        subjectAgentEmailConflict,
    ]);

    const resetCreateForm = () => {
        setCreateEmail("");
        setCreateRequestDate(dayjs().startOf("day"));
        setCreateRequestType("");
        setCreateRequestBy("");
        setCreateAuthAgentName("");
        setCreateAuthAgentEmail("");
        setCreateAuthFiles([]);
        setCreateDetails("");
        setCreateFileError("");
        setCreateEmailError("");
    };

    const handleOpenCreateModal = () => {
        resetCreateForm();
        setCreateOpen(true);
    };

    const handleCloseCreateModal = (
        _event: object,
        reason?: "backdropClick" | "escapeKeyDown"
    ) => {
        if (reason === "backdropClick") return;
        if (createSaving) return;
        setCreateOpen(false);
        resetCreateForm();
    };

    const handleCancelCreate = () => {
        if (createSaving) return;
        setCreateOpen(false);
        resetCreateForm();
    };

    const validateAndSetAuthFiles = (fileList: FileList | null) => {
        if (!fileList?.length) return;
        const incoming = Array.from(fileList);
        setCreateAuthFiles((prev) => {
            const { files, error } = mergeAuthFiles(prev, incoming);
            queueMicrotask(() => setCreateFileError(error));
            return files;
        });
    };

    const openAuthFilePicker = () => {
        if (createSaving) return;
        if (!authInputRef.current) return;
        authInputRef.current.value = "";
        authInputRef.current.click();
    };

    const removeAuthFileAt = (index: number) => {
        setCreateFileError("");
        setCreateAuthFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const [auditOpen, setAuditOpen] = useState(false);
    const [auditLoading, setAuditLoading] = useState(false);
    const [auditRows, setAuditRows] = useState<
        Array<{
            changedBy?: string;
            from?: string;
            to?: string;
            timestamp?: number | string;
        }>
    >([]);
    const [auditRequestId, setAuditRequestId] = useState<string>("");
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [detailsRequestId, setDetailsRequestId] = useState("");
    const [detailsText, setDetailsText] = useState("");
    const [downloadLoadingByRequestId, setDownloadLoadingByRequestId] = useState<
        Record<string, boolean>
    >({});
    const [privacyReportEmailLoadingByRequestId, setPrivacyReportEmailLoadingByRequestId] =
        useState<Record<string, boolean>>({});

    const hasActiveListFilters = useMemo(
        () =>
            searchEmail.trim().length > 0 ||
            filterRequestDate != null ||
            filterRequestStatus !== "" ||
            filterVerificationStatus !== "" ||
            filterRequestType !== "",
        [
            searchEmail,
            filterRequestDate,
            filterRequestStatus,
            filterVerificationStatus,
            filterRequestType,
        ]
    );

    const sortedAndFiltered = useMemo(() => {
        // Backend is the source of truth for pagination + filters.
        // We only sort within the current page for "earliest expiry on top".
        return [...requests].sort((a, b) => {
            const da = computedDaysRemaining(a);
            const db = computedDaysRemaining(b);
            if (da !== db) return da - db;
            return toMillis(a.requestDate) - toMillis(b.requestDate);
        });
    }, [requests, countdownTick]);

    useEffect(() => {
        if (!canView) return;
        getRequests({ cursor: "" });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canView]);

    useEffect(() => {
        const id = window.setInterval(() => setCountdownTick((t) => t + 1), 60_000);
        return () => window.clearInterval(id);
    }, []);

    const buildListParams = (
        cursor: string,
        limit: number,
        overrides?: {
            email?: string;
            requestDate?: string;
            requestStatus?: string;
            verificationStatus?: string;
            requestType?: string;
        }
    ) => {
        const params = new URLSearchParams();
        params.set("limit", String(limit));
        if (cursor) params.set("cursor", cursor);
        const email = overrides?.email ?? searchEmail;
        const requestStatus = overrides?.requestStatus ?? filterRequestStatus;
        const verificationStatus =
            overrides?.verificationStatus ?? filterVerificationStatus;
        const requestType = overrides?.requestType ?? filterRequestType;
        const requestDateStr =
            overrides?.requestDate !== undefined
                ? overrides.requestDate
                : filterRequestDate?.isValid()
                  ? filterRequestDate.format("YYYY-MM-DD")
                  : "";

        if (email.trim()) params.set("email", email.trim());
        if (requestDateStr.trim()) {
            params.set("requestDate", requestDateStr.trim());
            const requestDateTimezone =
                Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
            params.set("requestDateTimezone", requestDateTimezone);
        }
        if (requestStatus) params.set("requestStatus", requestStatus);
        if (verificationStatus) params.set("verificationStatus", verificationStatus);
        if (requestType) params.set("requestType", requestType);
        return params;
    };

    const getRequests = async (opts?: {
        cursor?: string;
        limit?: number;
        filters?: {
            email?: string;
            requestDate?: string;
            requestStatus?: string;
            verificationStatus?: string;
            requestType?: string;
        };
    }) => {
        const cursor = opts?.cursor ?? "";
        const limit = opts?.limit ?? rowsPerPage;
        setLoader(true);
        try {
            const params = buildListParams(cursor, limit, opts?.filters);
            const url = `${process.env.BASE_URL}api/v1/cms/dsar?${params.toString()}`;
            const response = await axios.get(url);
            const data = response.data;
            if (data?.success) {
                const list: any[] =
                    data?.data?.requests ||
                    data?.data?.items ||
                    data?.data?.list ||
                    [];

                const mapped: DsarRequest[] = list.map((r: any) => ({
                    requestId: r.requestId || r.id || r.request_id || "-",
                    email: r.email || r.emailAddress || r.userEmail || "-",
                    requestDate:
                        r.requestDate ||
                        r.createdAt ||
                        r.created_at ||
                        r.timestamp ||
                        0,
                    requestType: r.requestType || r.type || "-",
                    verificationStatus:
                        r.verificationStatus || r.verification || "Incomplete",
                    requestStatus: r.requestStatus || r.status || "Initiated",
                    requestRaisedBy: r.requestRaisedBy || r.raisedBy || r.requestedBy || "-",
                    daysRemaining: r.daysRemaining ?? r.days_remaining,
                    details: r.details || r.description || r.dataCorrectionDetails || "",
                    files: r.files || r.authorizationFiles || r.authorization_documents || null,
                    downloadableAuthAttachmentCount: (() => {
                        const v =
                            r.downloadableAuthAttachmentCount ??
                            r.downloadable_auth_attachment_count;
                        return typeof v === "number" && Number.isFinite(v) ? v : undefined;
                    })(),
                }));

                setRequests(mapped);
                const apiCount =
                    data?.data?.totalCount ??
                    data?.data?.count ??
                    data?.data?.total ??
                    null;
                const next =
                    data?.data?.nextCursor ??
                    data?.data?.next_cursor ??
                    data?.data?.nextToken ??
                    null;
                const resolvedNextCursor =
                    next !== null && next !== undefined && `${next}` !== "null"
                        ? `${next}`
                        : null;

                setNextCursor(resolvedNextCursor);

                if (typeof apiCount === "number" && Number.isFinite(apiCount)) {
                    setTotalCount(apiCount);
                } else if (!resolvedNextCursor && mapped.length === 0) {
                    setTotalCount(0);
                } else if (!resolvedNextCursor) {
                    // Fallback so pagination label matches screenshot on last page.
                    setTotalCount(page * limit + mapped.length);
                } else {
                    // Unknown total while more pages exist.
                    setTotalCount(-1);
                }
            } else {
                enqueueSnackbar(data?.message || "Failed to fetch DSAR requests", {
                    variant: "error",
                });
            }
        } catch (error: any) {
            console.error("Error fetching DSAR requests:", error);
            enqueueSnackbar(
                error?.response?.data?.message || "Failed to fetch DSAR requests",
                { variant: "error" }
            );
        } finally {
            setLoader(false);
        }
    };

    const resetPagination = () => {
        setPage(0);
        setCursorQueue([""]);
        setNextCursor(null);
        setTotalCount(0);
    };

    const refresh = async (filters?: {
        email?: string;
        requestDate?: string;
        requestStatus?: string;
        verificationStatus?: string;
        requestType?: string;
    }) => {
        resetPagination();
        setPage(0);
        await getRequests({ cursor: "", filters });
    };

    const handleChangePage = async (event: unknown, newPage: number) => {
        if (newPage < page) {
            const nextQueue = cursorQueue.slice(0, Math.max(1, newPage + 1));
            setCursorQueue(nextQueue);
            setPage(newPage);
            await getRequests({
                cursor: nextQueue[nextQueue.length - 1] || "",
            });
            return;
        }

        // Next page
        if (!nextCursor) return;
        const appended = [...cursorQueue, nextCursor];
        setCursorQueue(appended);
        setPage(newPage);
        await getRequests({ cursor: nextCursor });
    };

    const handleChangeRowsPerPage = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const n = Number.parseInt(event.target.value, 10);
        setRowsPerPage(n);
        resetPagination();
        await getRequests({ cursor: "", limit: n });
    };

    const canChangeStatus = (r: DsarRequest) => {
        const verificationIncomplete = `${r.verificationStatus}`.toLowerCase() !== "complete";
        const raisedByUser = `${r.requestRaisedBy}`.toLowerCase() === "user";
        if (raisedByUser && verificationIncomplete) return false;
        return true;
    };

    const updateStatus = async (r: DsarRequest, nextStatus: string) => {
        if (!canEdit) {
            enqueueSnackbar("You don’t have permission to update DSAR status.", {
                variant: "warning",
            });
            return;
        }
        if (!canChangeStatus(r) && nextStatus !== "Initiated") {
            enqueueSnackbar(
                "Status change is blocked until verification is complete for user-raised requests.",
                { variant: "warning" }
            );
            return;
        }

        try {
            setLoader(true);
            const url = `${process.env.BASE_URL}api/v1/cms/dsar/${encodeURIComponent(
                r.requestId
            )}`;
            const payload = { requestStatus: nextStatus };
            const resp = await axios.patch(url, payload);
            if (resp.data?.success) {
                enqueueSnackbar("Status updated.", { variant: "success" });
                await refresh();
            } else {
                enqueueSnackbar(resp.data?.message || "Failed to update status", {
                    variant: "error",
                });
            }
        } catch (error: any) {
            enqueueSnackbar(
                error?.response?.data?.message || "Failed to update status",
                { variant: "error" }
            );
        } finally {
            setLoader(false);
        }
    };

    const openPrivacyReportSendDialog = (r: DsarRequest) => {
        if (!canEdit) {
            enqueueSnackbar("You don’t have permission to send DSAR emails.", {
                variant: "warning",
            });
            return;
        }
        const reportTypeLabel = privacyReportEmailReportType(r);
        if (!reportTypeLabel) {
            enqueueSnackbar(
                "Request type must be Access or Correction to send the privacy report email.",
                { variant: "error" },
            );
            return;
        }
        const to = `${r.email || ""}`.trim();
        if (!isValidEmailFormat(to)) {
            enqueueSnackbar("This row does not have a valid recipient email.", {
                variant: "warning",
            });
            return;
        }
        setPrivacyReportSendRow(r);
        setPrivacyReportSendFile(null);
        setPrivacyReportSendFileError("");
        setPrivacyReportSendOpen(true);
        if (privacyReportFileInputRef.current) {
            privacyReportFileInputRef.current.value = "";
        }
    };

    const handleClosePrivacyReportSend = (
        _event: object,
        reason?: "backdropClick" | "escapeKeyDown",
    ) => {
        if (reason === "backdropClick") return;
        const rid = privacyReportSendRow?.requestId;
        if (rid && privacyReportEmailLoadingByRequestId[rid]) return;
        setPrivacyReportSendOpen(false);
        setPrivacyReportSendRow(null);
        setPrivacyReportSendFile(null);
        setPrivacyReportSendFileError("");
        if (privacyReportFileInputRef.current) {
            privacyReportFileInputRef.current.value = "";
        }
    };

    const cancelPrivacyReportSend = () => {
        const rid = privacyReportSendRow?.requestId;
        if (rid && privacyReportEmailLoadingByRequestId[rid]) return;
        setPrivacyReportSendOpen(false);
        setPrivacyReportSendRow(null);
        setPrivacyReportSendFile(null);
        setPrivacyReportSendFileError("");
        if (privacyReportFileInputRef.current) {
            privacyReportFileInputRef.current.value = "";
        }
    };

    const handlePrivacyReportFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f) {
            setPrivacyReportSendFile(null);
            setPrivacyReportSendFileError("");
            return;
        }
        const err = validatePrivacyReportAttachmentFile(f);
        if (err) {
            setPrivacyReportSendFileError(err);
            setPrivacyReportSendFile(null);
            return;
        }
        setPrivacyReportSendFileError("");
        setPrivacyReportSendFile(f);
    };

    const submitPrivacyReportDelivery = async () => {
        const r = privacyReportSendRow;
        if (!r) return;
        const reportTypeLabel = privacyReportEmailReportType(r);
        if (!reportTypeLabel) return;
        const to = `${r.email || ""}`.trim();
        if (!isValidEmailFormat(to)) return;
        if (!privacyReportSendFile) {
            setPrivacyReportSendFileError("Choose a report file to attach.");
            return;
        }
        setPrivacyReportEmailLoadingByRequestId((prev) => ({
            ...prev,
            [r.requestId]: true,
        }));
        try {
            const formData = new FormData();
            formData.append("to", to);
            formData.append("reportTypeLabel", reportTypeLabel);
            formData.append("accountEmail", to);
            formData.append("attachment", privacyReportSendFile);

            const url = `${process.env.BASE_URL}api/v1/cms/dsar/privacy-report-delivery`;
            const resp = await axios.post(url, formData);
            if (resp.data?.success) {
                enqueueSnackbar(
                    resp.data?.message || "Privacy report delivery email sent.",
                    { variant: "success" },
                );
                setPrivacyReportSendOpen(false);
                setPrivacyReportSendRow(null);
                setPrivacyReportSendFile(null);
                setPrivacyReportSendFileError("");
                if (privacyReportFileInputRef.current) {
                    privacyReportFileInputRef.current.value = "";
                }
            } else {
                enqueueSnackbar(
                    resp.data?.message || "Failed to send privacy report email.",
                    { variant: "error" },
                );
            }
        } catch (error: any) {
            enqueueSnackbar(
                error?.response?.data?.message || "Failed to send privacy report email.",
                { variant: "error" },
            );
        } finally {
            setPrivacyReportEmailLoadingByRequestId((prev) => {
                const next = { ...prev };
                delete next[r.requestId];
                return next;
            });
        }
    };

    const openAudit = async (requestId: string) => {
        setAuditOpen(true);
        setAuditRequestId(requestId);
        setAuditLoading(true);
        try {
            const url = `${process.env.BASE_URL}api/v1/cms/dsar/${encodeURIComponent(
                requestId
            )}/audit`;
            const resp = await axios.get(url);
            const data = resp.data;
            if (data?.success) {
                const list = extractAuditEntries(data?.data);
                setAuditRows(
                    list.map((row: any) => ({
                        changedBy:
                            row.actorEmail ||
                            row.changedBy ||
                            row.user ||
                            row.name ||
                            row.editor,
                        from: row.statusFrom ?? row.from ?? row.previousStatus,
                        to: row.statusTo ?? row.to ?? row.newStatus,
                        timestamp:
                            row.at ??
                            row.isoAt ??
                            row.timestamp ??
                            row.createdAt ??
                            row.time,
                    }))
                );
            } else {
                setAuditRows([]);
                enqueueSnackbar(data?.message || "Failed to load audit trail", {
                    variant: "error",
                });
            }
        } catch (error: any) {
            setAuditRows([]);
            enqueueSnackbar(
                error?.response?.data?.message || "Failed to load audit trail",
                { variant: "error" }
            );
        } finally {
            setAuditLoading(false);
        }
    };

    const openDetails = async (r: DsarRequest) => {
        setDetailsOpen(true);
        setDetailsRequestId(r.requestId);
        setDetailsText((r.details && String(r.details).trim()) || "");
        setDetailsLoading(true);
        try {
            const url = `${process.env.BASE_URL}api/v1/cms/dsar/${encodeURIComponent(r.requestId)}`;
            const resp = await axios.get(url);
            const payload = resp.data?.data;
            if (payload) {
                const raw =
                    payload.details ??
                    payload.privacyRequestForm?.details ??
                    payload.dataCorrectionDetails ??
                    "";
                setDetailsText(typeof raw === "string" ? raw : "");
            }
        } catch (error: any) {
            enqueueSnackbar(
                error?.response?.data?.message || "Could not load full details; showing list value.",
                { variant: "warning" }
            );
        } finally {
            setDetailsLoading(false);
        }
    };

    const downloadLoadingKey = (requestId: string, fileIndex: number) =>
        `${requestId}\u0000${fileIndex}`;

    /**
     * GET …/dsar/{requestId}/auth-document/presigned-url?expiresIn=…&fileIndex=…
     * - Omit `fileIndex` or use `0` for the first attachment (backward compatible); we omit when index is 0.
     * - Server also accepts alias query `index` (same meaning).
     * - Response `data`: { url, expiresIn, fileIndex, attachmentCount }.
     */
    const downloadAuthDocument = async (requestId: string, fileIndex = 0) => {
        const lk = downloadLoadingKey(requestId, fileIndex);
        setDownloadLoadingByRequestId((prev) => ({ ...prev, [lk]: true }));
        try {
            enqueueSnackbar("Fetching download link…", { variant: "info" });
            const base = process.env.BASE_URL || "";
            const q = new URLSearchParams({ expiresIn: "3600" });
            if (fileIndex > 0) {
                q.set("fileIndex", String(fileIndex));
            }
            const path = `api/v1/cms/dsar/${encodeURIComponent(
                requestId
            )}/auth-document/presigned-url?${q.toString()}`;
            const url = base ? `${base}${path}` : `/${path}`;
            const resp = await axios.get(url);
            const payload = resp.data?.data ?? resp.data;
            const presignedUrl =
                payload?.url ||
                payload?.presignedUrl ||
                resp.data?.url ||
                resp.data?.presignedUrl;
            if (!presignedUrl) {
                enqueueSnackbar("Failed to get download URL.", {
                    variant: "error",
                });
                return;
            }
            window.open(presignedUrl, "_blank", "noopener,noreferrer");
        } catch (error: any) {
            enqueueSnackbar(
                error?.response?.data?.message ||
                    "Failed to download authorization document.",
                { variant: "error" }
            );
        } finally {
            setDownloadLoadingByRequestId((prev) => ({
                ...prev,
                [lk]: false,
            }));
        }
    };

    const createRequest = async () => {
        const email = createEmail.trim();
        setCreateEmailError("");
        if (!isCreateFormValid) {
            enqueueSnackbar("Please complete all required fields.", { variant: "warning" });
            return;
        }
        const apiRequestType = mapUiRequestTypeToApi(createRequestType);

        try {
            setCreateSaving(true);
            const url = `${process.env.BASE_URL}api/v1/cms/dsar`;

            {
                const fd = new FormData();
                fd.append("email", email);
                // Multipart: optional requestDate (same rules as JSON); epoch ms string.
                if (createRequestDate?.isValid()) {
                    fd.append("requestDate", String(createRequestDate.valueOf()));
                }
                fd.append("requestType", createRequestType || apiRequestType);
                if (createAuthAgentName.trim())
                    fd.append("authAgentName", createAuthAgentName.trim());
                if (createAuthAgentEmail.trim())
                    fd.append("authAgentEmail", createAuthAgentEmail.trim());
                // Match server DETAILS_MAX_LENGTH; slice so multipart never exceeds limit (defense if textarea maxLength is bypassed).
                if (apiRequestType === "Correction") {
                    fd.append(
                        "details",
                        createDetails.trim().slice(0, DSAR_CREATE_DETAILS_MAX_LENGTH)
                    );
                }

                createAuthFiles.forEach((f) => {
                    fd.append("authDocument", f);
                });

                // Let axios set multipart boundary; a bare `multipart/form-data` header breaks parsing.
                const resp = await axios.post(url, fd);
                if (resp.data?.success) {
                    enqueueSnackbar(
                        resp.data?.message || "DSAR request created successfully.",
                        { variant: "success" }
                    );
                    setCreateOpen(false);
                    resetCreateForm();
                    await refresh();
                    return;
                }
                const msg = resp.data?.message || "Failed to create request";
                if (resp.data?.code === "DSAR_CONFLICT") {
                    setCreateEmailError(msg);
                    return;
                }
                if (/account|not associated|wonder|not found|unknown user|invalid.*email/i.test(msg)) {
                    setCreateEmailError(
                        msg.includes("account") || msg.includes("Wonder")
                            ? msg
                            : "This email address is not associated with a Wonder account-holder."
                    );
                } else {
                    enqueueSnackbar(msg, { variant: "error" });
                }
            }

        } catch (error: any) {
            const data = error?.response?.data;
            const msg =
                (typeof data?.message === "string" && data.message) ||
                data?.error?.message ||
                error?.message ||
                "Failed to create request";
            const code = data?.code || data?.error?.code;
            if (code === "DSAR_CONFLICT") {
                setCreateEmailError(
                    typeof msg === "string" && msg.length < 280
                        ? msg
                        : "A request for this email is already in progress."
                );
                return;
            }
            const isAccountError =
                code === "ACCOUNT_NOT_FOUND" ||
                code === "USER_NOT_FOUND" ||
                /account|not associated|wonder|not found|no user|unknown.*email|does not exist/i.test(
                    `${msg}`
                );
            if (isAccountError) {
                setCreateEmailError(
                    typeof msg === "string" && msg.length < 280
                        ? msg
                        : "This email address is not associated with a Wonder account-holder."
                );
            } else {
                enqueueSnackbar(`${msg}`, { variant: "error" });
            }
        } finally {
            setCreateSaving(false);
        }
    };

    if (!canView) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography variant="h6">DSAR Dashboard</Typography>
                <Typography sx={{ mt: 1 }}>
                    You don’t have access to this module.
                </Typography>
            </Box>
        );
    }

    return (
        <div className="config-heading">
            <div className="header-content-list">
                <div>
                    <h3>DSAR Dashboard</h3>
                </div>
                <div className="d-flex j-center" style={{ gap: 12 }}>
                    <Button
                        variant="contained"
                        disabled={!canEdit}
                        onClick={handleOpenCreateModal}
                    >
                        Add New Request
                    </Button>
                </div>
            </div>

            <Paper
                sx={{
                    p: 2,
                    mb: 2,
                    width: "100%",
                    maxWidth: "100%",
                    boxSizing: "border-box",
                    overflow: "visible",
                }}
            >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box
                    sx={{
                        display: "grid",
                        width: "100%",
                        minWidth: 0,
                        gap: 2,
                        gridTemplateColumns: {
                            xs: "minmax(0, 1fr)",
                            md: "repeat(3, minmax(0, 1fr))",
                        },
                    }}
                >
                    <TextField
                        size="small"
                        name="searchEmail"
                        placeholder="Search by email (partial ok)"
                        variant="outlined"
                        label="Email Address"
                        value={searchEmail}
                        fullWidth
                        onChange={(e) => setSearchEmail(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter")
                                void refresh({
                                    email: (e.target as HTMLInputElement).value,
                                    requestDate: filterRequestDate?.isValid()
                                        ? filterRequestDate.format("YYYY-MM-DD")
                                        : "",
                                    requestStatus: filterRequestStatus,
                                    verificationStatus: filterVerificationStatus,
                                    requestType: filterRequestType,
                                });
                        }}
                        InputProps={{
                            endAdornment: (
                                <>
                                    {searchEmail && (
                                        <HighlightOffIcon
                                            className="cursor-pointer"
                                            onClick={() => {
                                                setSearchEmail("");
                                                void refresh({
                                                    email: "",
                                                    requestStatus: filterRequestStatus,
                                                    verificationStatus:
                                                        filterVerificationStatus,
                                                    requestType: filterRequestType,
                                                });
                                            }}
                                        />
                                    )}
                                    <SearchIcon
                                        className="cursor-pointer ml-1"
                                        onClick={() =>
                                            void refresh({
                                                email: searchEmail,
                                                requestDate: filterRequestDate?.isValid()
                                                    ? filterRequestDate.format("YYYY-MM-DD")
                                                    : "",
                                                requestStatus: filterRequestStatus,
                                                verificationStatus:
                                                    filterVerificationStatus,
                                                requestType: filterRequestType,
                                            })
                                        }
                                    />
                                </>
                            ),
                        }}
                        sx={{ minWidth: 0 }}
                    />
                    <Box sx={{ minWidth: 0, width: "100%" }}>
                        <DatePicker
                            label="Request date"
                            value={filterRequestDate}
                            onChange={(v) =>
                                setFilterRequestDate(
                                    v?.isValid() ? v.startOf("day") : null
                                )
                            }
                            minDate={dayjs()
                                .subtract(MAX_REQUESTED_DATE_LOOKBACK_DAYS, "day")
                                .startOf("day")}
                            maxDate={dayjs().startOf("day")}
                            slotProps={{
                                textField: {
                                    size: "small",
                                    fullWidth: true,
                                },
                            }}
                        />
                    </Box>
                    <FormControl size="small" fullWidth sx={{ minWidth: 0 }}>
                        <InputLabel id="dsar-filter-status">Request Status</InputLabel>
                        <Select
                            labelId="dsar-filter-status"
                            label="Request Status"
                            value={filterRequestStatus}
                            onChange={(e) =>
                                setFilterRequestStatus(`${e.target.value}`)
                            }
                        >
                            <MenuItem value="">All</MenuItem>
                            {STATUS_OPTIONS.map((s) => (
                                <MenuItem key={s} value={s}>
                                    {s}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" fullWidth sx={{ minWidth: 0 }}>
                        <InputLabel id="dsar-filter-verification">
                            Verification Status
                        </InputLabel>
                        <Select
                            labelId="dsar-filter-verification"
                            label="Verification Status"
                            value={filterVerificationStatus}
                            onChange={(e) =>
                                setFilterVerificationStatus(`${e.target.value}`)
                            }
                        >
                            <MenuItem value="">All</MenuItem>
                            {VERIFICATION_OPTIONS.map((s) => (
                                <MenuItem key={s} value={s}>
                                    {s}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" fullWidth sx={{ minWidth: 0 }}>
                        <InputLabel id="dsar-filter-type">Request Type</InputLabel>
                        <Select
                            labelId="dsar-filter-type"
                            label="Request Type"
                            value={filterRequestType}
                            onChange={(e) => setFilterRequestType(`${e.target.value}`)}
                        >
                            <MenuItem value="">All</MenuItem>
                            {REQUEST_TYPE_OPTIONS.map((t) => (
                                <MenuItem key={t} value={t}>
                                    {t}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Stack
                        direction="row"
                        spacing={1}
                        flexWrap="wrap"
                        useFlexGap
                        sx={{
                            minWidth: 0,
                            width: "100%",
                            alignItems: "center",
                            alignSelf: { xs: "stretch", md: "end" },
                            justifyContent: "flex-start",
                        }}
                    >
                        <Button
                            variant="contained"
                            onClick={() =>
                                void refresh({
                                    email: searchEmail,
                                    requestDate: filterRequestDate?.isValid()
                                        ? filterRequestDate.format("YYYY-MM-DD")
                                        : "",
                                    requestStatus: filterRequestStatus,
                                    verificationStatus: filterVerificationStatus,
                                    requestType: filterRequestType,
                                })
                            }
                            disabled={loader}
                        >
                            Apply filters
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                setFilterRequestStatus("");
                                setFilterVerificationStatus("");
                                setFilterRequestType("");
                                setSearchEmail("");
                                setFilterRequestDate(null);
                                void refresh({
                                    email: "",
                                    requestDate: "",
                                    requestStatus: "",
                                    verificationStatus: "",
                                    requestType: "",
                                });
                            }}
                            disabled={loader}
                        >
                            Clear filters
                        </Button>
                    </Stack>
                </Box>
                </LocalizationProvider>
            </Paper>

            {loader && <CircularProgress size={30} />}

            <TableContainer
                component={Paper}
                sx={{ maxHeight: "70vh", overflowY: "auto" }}
            >
                <Table stickyHeader aria-label="dsar table">
                    <TableHead>
                        <TableRow>
                            <TableCell><h3>Request ID</h3></TableCell>
                            <TableCell><h3>Email Address</h3></TableCell>
                            <TableCell><h3>Request Date</h3></TableCell>
                            <TableCell><h3>Request Type</h3></TableCell>
                            <TableCell><h3>Verification Status</h3></TableCell>
                            <TableCell><h3>Request Status</h3></TableCell>
                            <TableCell><h3>Request Raised By</h3></TableCell>
                            <TableCell><h3>Days Remaining</h3></TableCell>
                            <TableCell><h3>Details</h3></TableCell>
                            <TableCell><h3>Files</h3></TableCell>
                            <TableCell><h3>Action</h3></TableCell>
                            <TableCell><h3>Audit Trail</h3></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedAndFiltered.length === 0 && !loader && (
                            <TableRow>
                                <TableCell colSpan={12}>
                                    <Typography
                                        sx={{ py: 3, textAlign: "center" }}
                                        color="text.secondary"
                                    >
                                        {hasActiveListFilters
                                            ? "No requests match your filters. Try a different email substring, request date, or status."
                                            : "No DSAR requests found."}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}

                        {sortedAndFiltered.map((r) => {
                            const days = computedDaysRemaining(r);
                            /** Red emphasis for 0–10 days remaining (including 0), regardless of status. */
                            const daysRemainingUrgent = days <= 10;

                            const verificationBlocksProgress =
                                !canChangeStatus(r) &&
                                `${r.requestStatus}` === "Initiated";
                            const disabledStatusChange = !canEdit || verificationBlocksProgress;

                            let disabledReason = "";
                            if (!canEdit) disabledReason = "You don’t have edit permission.";
                            else if (!canChangeStatus(r))
                                disabledReason =
                                    "Blocked until verification is complete (User-raised).";

                            const sendEmailOk = canSendDsarEmail(r);
                            const privacyReportSending =
                                !!privacyReportEmailLoadingByRequestId[r.requestId];

                            return (
                                <TableRow key={r.requestId}>
                                    <TableCell>{r.requestId}</TableCell>
                                    <TableCell>{r.email}</TableCell>
                                    <TableCell>{formatDate(r.requestDate)}</TableCell>
                                    <TableCell>{r.requestType}</TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={r.verificationStatus || "Incomplete"}
                                            color={
                                                `${r.verificationStatus}`.toLowerCase() ===
                                                "complete"
                                                    ? "success"
                                                    : "default"
                                            }
                                        />
                                    </TableCell>
                                    <TableCell>{r.requestStatus}</TableCell>
                                    <TableCell>{r.requestRaisedBy}</TableCell>
                                    <TableCell
                                        sx={(theme) =>
                                            daysRemainingUrgent
                                                ? {
                                                      color: "error.main",
                                                      fontWeight: 700,
                                                      bgcolor: alpha(
                                                          theme.palette.error.main,
                                                          0.12
                                                      ),
                                                  }
                                                : {}
                                        }
                                    >
                                        {days}
                                    </TableCell>
                                    <TableCell>
                                        <Button
                                            variant="text"
                                            size="small"
                                            onClick={() => openDetails(r)}
                                        >
                                            View
                                        </Button>
                                    </TableCell>
                                    <TableCell>
                                        {(() => {
                                            const n = downloadableAttachmentCountForRow(r);
                                            if (n <= 0)
                                                return (
                                                    <Typography
                                                        component="span"
                                                        variant="body2"
                                                        color="text.secondary"
                                                    >
                                                        No file
                                                    </Typography>
                                                );
                                            return (
                                                <Stack
                                                    direction="row"
                                                    flexWrap="wrap"
                                                    gap={0.5}
                                                    alignItems="center"
                                                >
                                                    {Array.from({ length: n }, (_, i) => {
                                                        const lk = downloadLoadingKey(
                                                            r.requestId,
                                                            i
                                                        );
                                                        const loading =
                                                            !!downloadLoadingByRequestId[lk];
                                                        return (
                                                            <Tooltip
                                                                key={lk}
                                                                title={`Download attachment ${i + 1}`}
                                                            >
                                                                <span>
                                                                    <Button
                                                                        variant="text"
                                                                        size="small"
                                                                        disabled={loading}
                                                                        onClick={() =>
                                                                            downloadAuthDocument(
                                                                                r.requestId,
                                                                                i
                                                                            )
                                                                        }
                                                                        startIcon={
                                                                            loading ? (
                                                                                <CircularProgress
                                                                                    size={16}
                                                                                />
                                                                            ) : (
                                                                                <DownloadOutlinedIcon />
                                                                            )
                                                                        }
                                                                    >
                                                                        {authDownloadButtonLabel(
                                                                            loading,
                                                                            n,
                                                                            i
                                                                        )}
                                                                    </Button>
                                                                </span>
                                                            </Tooltip>
                                                        );
                                                    })}
                                                </Stack>
                                            );
                                        })()}
                                    </TableCell>
                                    <TableCell>
                                        <Stack spacing={1} sx={{ minWidth: 160 }}>
                                            <Tooltip title={disabledReason}>
                                                <span>
                                                    <FormControl
                                                        size="small"
                                                        fullWidth
                                                        sx={{ minWidth: 160 }}
                                                    >
                                                        <InputLabel id={`status-${r.requestId}`}>
                                                            Update
                                                        </InputLabel>
                                                        <Select
                                                            labelId={`status-${r.requestId}`}
                                                            label="Update"
                                                            value={r.requestStatus || "Initiated"}
                                                            disabled={disabledStatusChange}
                                                            onChange={(e) =>
                                                                updateStatus(r, `${e.target.value}`)
                                                            }
                                                        >
                                                            {allowedNextStatuses(
                                                                `${r.requestStatus || "Initiated"}`
                                                            ).map((s) => (
                                                                <MenuItem key={s} value={s}>
                                                                    {s}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </span>
                                            </Tooltip>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        fullWidth
                                                        startIcon={
                                                            privacyReportSending ? (
                                                                <CircularProgress size={16} />
                                                            ) : (
                                                                <MailOutlineIcon />
                                                            )
                                                        }
                                                        disabled={
                                                            !sendEmailOk || privacyReportSending
                                                        }
                                                        onClick={() =>
                                                            openPrivacyReportSendDialog(r)
                                                        }
                                                    >
                                                        Send Email
                                                    </Button>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Button
                                            variant="text"
                                            onClick={() => openAudit(r.requestId)}
                                        >
                                            View
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                rowsPerPageOptions={[25, 50, 100]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                slotProps={{
                    actions: {
                        nextButton: {
                            disabled: !nextCursor,
                        },
                    },
                }}
            />

            <Dialog
                {...DSAR_DIALOG_LAYOUT_PROPS}
                maxWidth="sm"
                fullWidth
                open={createOpen}
                onClose={handleCloseCreateModal}
            >
                <DialogTitle>Add New Request</DialogTitle>
                <DialogContent>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            <TextField
                                required
                                label="User email address"
                                type="email"
                                value={createEmail}
                                onChange={(e) => {
                                    setCreateEmail(e.target.value);
                                    setCreateEmailError("");
                                }}
                                error={Boolean(createEmailError)}
                                helperText={
                                    createEmailError ||
                                    "Email of the Wonder account-holder for this DSAR."
                                }
                                fullWidth
                                disabled={createSaving}
                            />
                            <DatePicker
                                label="Request date"
                                value={createRequestDate}
                                onChange={(v) =>
                                    setCreateRequestDate(
                                        v?.isValid() ? v.startOf("day") : null
                                    )
                                }
                                minDate={dayjs()
                                    .subtract(MAX_REQUESTED_DATE_LOOKBACK_DAYS, "day")
                                    .startOf("day")}
                                maxDate={dayjs().startOf("day")}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        required: true,
                                        helperText:
                                            "When the subscriber or agent raised this request (not in the future; up to 2 years in the past).",
                                    },
                                }}
                                disabled={createSaving}
                            />
                            <FormControl
                                fullWidth
                                required
                                disabled={createSaving}
                                variant="outlined"
                            >
                                <InputLabel id="dsar-create-type" shrink>
                                    Request Type
                                </InputLabel>
                                <Select
                                    labelId="dsar-create-type"
                                    label="Request Type"
                                    value={createRequestType}
                                    displayEmpty
                                    renderValue={(selected) =>
                                        selected ? (
                                            `${selected}`
                                        ) : (
                                            <Typography
                                                component="span"
                                                sx={{ color: "text.secondary" }}
                                            >
                                                Select request type
                                            </Typography>
                                        )
                                    }
                                    onChange={(e) => {
                                        const v = `${e.target.value}`;
                                        setCreateRequestType(v);
                                        if (!isCorrectionRequestType(v)) {
                                            setCreateDetails("");
                                        }
                                    }}
                                >
                                    <MenuItem value="" sx={{ display: "none" }} aria-hidden />
                                    {CREATE_REQUEST_TYPE_OPTIONS.map((t) => (
                                        <MenuItem key={t} value={t}>
                                            {t}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {isCorrectionRequestType(createRequestType) && (
                                <TextField
                                    required
                                    label="Details"
                                    value={createDetails}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        setCreateDetails(
                                            v.length > DSAR_CREATE_DETAILS_MAX_LENGTH
                                                ? v.slice(0, DSAR_CREATE_DETAILS_MAX_LENGTH)
                                                : v
                                        );
                                    }}
                                    multiline
                                    minRows={3}
                                    fullWidth
                                    disabled={createSaving}
                                    inputProps={{
                                        maxLength: DSAR_CREATE_DETAILS_MAX_LENGTH,
                                    }}
                                    helperText={`Describe the correction requested (required for Data Correction). ${createDetails.length} / ${DSAR_CREATE_DETAILS_MAX_LENGTH} characters.`}
                                />
                            )}
                            <FormControl
                                fullWidth
                                required
                                disabled={createSaving}
                                variant="outlined"
                            >
                                <InputLabel id="dsar-create-by" shrink>
                                    Request By
                                </InputLabel>
                                <Select
                                    labelId="dsar-create-by"
                                    label="Request By"
                                    value={createRequestBy}
                                    displayEmpty
                                    renderValue={(selected) =>
                                        selected ? (
                                            `${selected}`
                                        ) : (
                                            <Typography
                                                component="span"
                                                sx={{ color: "text.secondary" }}
                                            >
                                                Select request by
                                            </Typography>
                                        )
                                    }
                                    onChange={(e) => {
                                        setCreateRequestBy(`${e.target.value}`);
                                        setCreateFileError("");
                                    }}
                                >
                                    <MenuItem value="" sx={{ display: "none" }} aria-hidden />
                                    {CREATE_REQUEST_BY_OPTIONS.map((t) => (
                                        <MenuItem key={t} value={t}>
                                            {t}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {createRequestBy === "Agent" && (
                                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                    <TextField
                                        label="Authorization agent name"
                                        value={createAuthAgentName}
                                        onChange={(e) => setCreateAuthAgentName(e.target.value)}
                                        fullWidth
                                        disabled={createSaving}
                                    />
                                    <TextField
                                        label="Authorization agent email"
                                        value={createAuthAgentEmail}
                                        onChange={(e) => setCreateAuthAgentEmail(e.target.value)}
                                        error={
                                            subjectAgentEmailConflict ||
                                            (Boolean(createAuthAgentEmail.trim()) &&
                                                !isValidEmailFormat(createAuthAgentEmail))
                                        }
                                        helperText={
                                            subjectAgentEmailConflict
                                                ? "Must not be the same as the user email address."
                                                : createAuthAgentEmail.trim() &&
                                                    !isValidEmailFormat(createAuthAgentEmail)
                                                  ? "Enter a valid email address."
                                                  : ""
                                        }
                                        fullWidth
                                        disabled={createSaving}
                                    />
                                </Stack>
                            )}
                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                    Authorization Documents
                                    {authRequired ? (
                                        <Typography component="span" color="error.main">
                                            {" "}
                                            *
                                        </Typography>
                                    ) : (
                                        <Typography
                                            component="span"
                                            color="text.secondary"
                                            variant="body2"
                                        >
                                            {" "}
                                            (optional)
                                        </Typography>
                                    )}
                                </Typography>
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                    <Button
                                        variant="outlined"
                                        onClick={openAuthFilePicker}
                                        disabled={
                                            createSaving ||
                                            createAuthFiles.length >= MAX_AUTH_FILES_PER_REQUEST
                                        }
                                    >
                                        {createAuthFiles.length === 0
                                            ? "Choose files"
                                            : "Add more files"}
                                    </Button>
                                    <Typography variant="body2" color="text.secondary">
                                        {formatAuthFileSelectionSummary(createAuthFiles.length)} (
                                        {createAuthFiles.length}/{MAX_AUTH_FILES_PER_REQUEST})
                                    </Typography>
                                </Stack>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Up to {MAX_AUTH_FILES_PER_REQUEST} files per request (PDF, JPG, PNG,
                                    DOC, DOCX).
                                </Typography>
                                <input
                                    ref={authInputRef}
                                    type="file"
                                    multiple
                                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,application/pdf,image/*"
                                    disabled={
                                        createSaving ||
                                        createAuthFiles.length >= MAX_AUTH_FILES_PER_REQUEST
                                    }
                                    onChange={(e) => {
                                        validateAndSetAuthFiles(e.target.files);
                                        e.target.value = "";
                                    }}
                                    style={{ display: "none" }}
                                />
                                {createFileError && (
                                    <Typography color="error" variant="caption" display="block" sx={{ mt: 0.5 }}>
                                        {createFileError}
                                    </Typography>
                                )}
                                {createAuthFiles.length > 0 && (
                                    <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1 }}>
                                        {createAuthFiles.map((f, idx) => (
                                            <Tooltip key={authFileFingerprint(f)} title={f.name}>
                                                <Chip
                                                    label={f.name}
                                                    onDelete={
                                                        createSaving
                                                            ? undefined
                                                            : () => removeAuthFileAt(idx)
                                                    }
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{
                                                        maxWidth: "100%",
                                                        "& .MuiChip-label": {
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap",
                                                            display: "block",
                                                        },
                                                    }}
                                                />
                                            </Tooltip>
                                        ))}
                                    </Stack>
                                )}
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                After submit, a verification email is sent to the account-holder
                                (handled by backend). Request status will be Initiated.
                            </Typography>
                        </Stack>
                    </LocalizationProvider>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={handleCancelCreate}
                        disabled={createSaving}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={createRequest}
                        disabled={createSaving || !isCreateFormValid}
                        startIcon={
                            createSaving ? (
                                <CircularProgress size={18} sx={{ color: "white" }} />
                            ) : undefined
                        }
                    >
                        Submit
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                {...DSAR_DIALOG_LAYOUT_PROPS}
                maxWidth="sm"
                fullWidth
                open={privacyReportSendOpen}
                onClose={handleClosePrivacyReportSend}
            >
                <DialogTitle>Send privacy report email</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {privacyReportSendRow ? (
                            <>
                                <Typography variant="body2" color="text.secondary">
                                    Request <strong>{privacyReportSendRow.requestId}</strong>
                                </Typography>
                                <TextField
                                    label="To"
                                    value={privacyReportSendRow.email}
                                    fullWidth
                                    InputProps={{ readOnly: true }}
                                />
                                <TextField
                                    label="Report type"
                                    value={
                                        privacyReportEmailReportType(privacyReportSendRow) ?? "—"
                                    }
                                    fullWidth
                                    InputProps={{ readOnly: true }}
                                />
                                <Box>
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems="center"
                                        flexWrap="wrap"
                                    >
                                        <Button
                                            variant="outlined"
                                            onClick={() =>
                                                privacyReportFileInputRef.current?.click()
                                            }
                                            disabled={
                                                !!privacyReportEmailLoadingByRequestId[
                                                    privacyReportSendRow.requestId
                                                ]
                                            }
                                        >
                                            Choose report file
                                        </Button>
                                        <Typography variant="body2" color="text.secondary">
                                            {privacyReportSendFile
                                                ? privacyReportSendFile.name
                                                : "PDF or DOCX (max 10 MB per service default)"}
                                        </Typography>
                                    </Stack>
                                    <input
                                        ref={privacyReportFileInputRef}
                                        type="file"
                                        accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                        style={{ display: "none" }}
                                        disabled={
                                            !!privacyReportEmailLoadingByRequestId[
                                                privacyReportSendRow.requestId
                                            ]
                                        }
                                        onChange={handlePrivacyReportFileChange}
                                    />
                                    {privacyReportSendFileError ? (
                                        <Typography
                                            color="error"
                                            variant="caption"
                                            display="block"
                                            sx={{ mt: 0.5 }}
                                        >
                                            {privacyReportSendFileError}
                                        </Typography>
                                    ) : null}
                                </Box>
                            </>
                        ) : null}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={cancelPrivacyReportSend}
                        disabled={
                            !!(
                                privacyReportSendRow &&
                                privacyReportEmailLoadingByRequestId[
                                    privacyReportSendRow.requestId
                                ]
                            )
                        }
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={submitPrivacyReportDelivery}
                        disabled={
                            !privacyReportSendRow ||
                            !privacyReportSendFile ||
                            !!(
                                privacyReportSendRow &&
                                privacyReportEmailLoadingByRequestId[
                                    privacyReportSendRow.requestId
                                ]
                            )
                        }
                        startIcon={
                            privacyReportSendRow &&
                            privacyReportEmailLoadingByRequestId[
                                privacyReportSendRow.requestId
                            ] ? (
                                <CircularProgress size={18} sx={{ color: "white" }} />
                            ) : undefined
                        }
                    >
                        Send
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                {...DSAR_DIALOG_LAYOUT_PROPS}
                maxWidth="md"
                fullWidth
                open={detailsOpen}
                onClose={() => setDetailsOpen(false)}
            >
                <DialogTitle>
                    Details{detailsRequestId ? ` • ${detailsRequestId}` : ""}
                </DialogTitle>
                <DialogContent>
                    {detailsLoading ? (
                        <CircularProgress size={28} sx={{ display: "block", my: 2 }} />
                    ) : (
                        <Typography
                            component="div"
                            sx={{
                                mt: 1,
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                            }}
                        >
                            {detailsText.trim()
                                ? detailsText
                                : "No details provided for this request."}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" onClick={() => setDetailsOpen(false)}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                {...DSAR_DIALOG_LAYOUT_PROPS}
                maxWidth="md"
                fullWidth
                open={auditOpen}
                onClose={() => setAuditOpen(false)}
            >
                <DialogTitle>Audit trail {auditRequestId ? `• ${auditRequestId}` : ""}</DialogTitle>
                <DialogContent>
                    {auditLoading ? (
                        <CircularProgress size={24} />
                    ) : auditRows.length === 0 ? (
                        <Typography sx={{ py: 2 }}>No audit records.</Typography>
                    ) : (
                        <TableContainer component={Paper} sx={{ mt: 1 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell><h3>Changed By</h3></TableCell>
                                        <TableCell><h3>Status From</h3></TableCell>
                                        <TableCell><h3>Status To</h3></TableCell>
                                        <TableCell><h3>Timestamp</h3></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {auditRows.map((a) => (
                                        <TableRow
                                            key={`${a.timestamp ?? ""}__${a.from ?? ""}__${a.to ?? ""}__${a.changedBy ?? ""}`}
                                        >
                                            <TableCell>{a.changedBy || "-"}</TableCell>
                                            <TableCell>{a.from || "-"}</TableCell>
                                            <TableCell>{a.to || "-"}</TableCell>
                                            <TableCell>
                                                {a.timestamp
                                                    ? formatAuditTimestamp(a.timestamp)
                                                    : "-"}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" onClick={() => setAuditOpen(false)}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

